/**
 * デバイススクリプトテンプレート
 *
 * テンプレートファイル（参照用）:
 *   - README.txt.template
 *   - setup.sh.template
 *   - connect-test.sh.template
 *
 * プレースホルダー:
 *   {{thingName}} - デバイス名
 *   {{endpoint}}  - IoT Coreエンドポイント
 *   {{region}}    - AWSリージョン
 */

// README.txt テンプレート
export const README_TEMPLATE = `============================================================
  {{thingName}} - IoT デバイス証明書パッケージ
============================================================

【含まれるファイル】
  {{thingName}}.pem.crt  - デバイス証明書
  {{thingName}}.pem.key  - 秘密鍵 (重要: 安全に保管)
  AmazonRootCA1.pem           - Amazon Root CA
  config.json                 - 設定ファイル
  setup.sh                    - セットアップスクリプト
  connect-test.sh             - 接続テストスクリプト (curl使用)
  README.txt                  - このファイル

【クイックスタート】

  1. ZIPファイルを解凍
     unzip {{thingName}}-certs.zip -d {{thingName}}
     cd {{thingName}}

  2. セットアップ実行（パーミッション設定）
     chmod +x setup.sh connect-test.sh
     ./setup.sh

  3. 接続テスト
     ./connect-test.sh

  ※ スクリプトは解凍したディレクトリで直接実行します
  ※ 別の場所にコピーする必要はありません

【接続テストオプション】

  ./connect-test.sh           # 基本接続テスト
  ./connect-test.sh publish   # メッセージ送信テスト
  ./connect-test.sh shadow    # Device Shadow 取得
  ./connect-test.sh update    # Device Shadow 更新
  ./connect-test.sh all       # 全テスト実行

【config.json の内容】

  thingName: {{thingName}}
  endpoint:  {{endpoint}}
  region:    {{region}}

【必要なパッケージ】

  追加パッケージ不要 (curl のみ使用)

【別のディレクトリで使用する場合】

  このフォルダごと移動またはコピーしてください:
  mv {{thingName}}/ /opt/iot-certs/
  cd /opt/iot-certs/{{thingName}}
  ./connect-test.sh

【トラブルシューティング】

  ■ 権限エラー (Permission denied) が出る場合:

    1. ファイル所有者を確認
       ls -la

    2. 所有者が自分でない場合は変更
       sudo chown -R $(whoami):$(whoami) .

    3. パーミッションを再設定
       chmod 600 *.pem.key
       chmod 644 *.pem.crt *.pem config.json

  ■ 接続できない場合:

    1. ファイルが揃っているか確認
       ls -la

    2. 秘密鍵のパーミッションを確認
       ls -la *.pem.key
       (600 である必要があります)

    3. ネットワーク接続を確認
       curl -I https://{{endpoint}}:8443/

    4. 証明書の有効期限確認
       openssl x509 -in {{thingName}}.pem.crt -noout -dates

    5. 手動で接続テスト
       curl --tlsv1.2 \\
         --cert {{thingName}}.pem.crt \\
         --key {{thingName}}.pem.key \\
         --cacert AmazonRootCA1.pem \\
         https://{{endpoint}}:8443/things/{{thingName}}/shadow

【注意事項】

  ・秘密鍵 (*.pem.key) は厳重に管理してください
  ・秘密鍵のパーミッションは 600 (所有者のみ読み書き可) が必須です
  ・sudo で解凍した場合、ファイル所有者が root になることがあります
  ・その場合は上記の手順で所有者を変更してください

============================================================
`;

// setup.sh テンプレート
export const SETUP_TEMPLATE = `#!/bin/bash
# ============================================================
# {{thingName}} セットアップスクリプト
#
# 使用方法:
#   unzip {{thingName}}-certs.zip -d {{thingName}}
#   cd {{thingName}}
#   chmod +x setup.sh connect-test.sh
#   ./setup.sh
# ============================================================

set -e

# スクリプトのあるディレクトリを取得（証明書格納場所）
SCRIPT_DIR="$(cd "$(dirname "$0")" && pwd)"
CERT_DIR="\${SCRIPT_DIR}"
THING_NAME="{{thingName}}"

echo ""
echo "============================================"
echo "  IoT デバイス セットアップ"
echo "  Thing: \${THING_NAME}"
echo "  証明書ディレクトリ: \${CERT_DIR}"
echo "============================================"
echo ""

# 1. 必要なファイルの存在確認
echo "[1/3] ファイル確認..."
required_files=("{{thingName}}.pem.crt" "{{thingName}}.pem.key" "AmazonRootCA1.pem" "config.json")
missing=0
for f in "\${required_files[@]}"; do
  if [ ! -f "\${CERT_DIR}/\${f}" ]; then
    echo "  エラー: \${f} が見つかりません"
    missing=1
  fi
done

if [ \${missing} -eq 1 ]; then
  echo ""
  echo "必要なファイルが不足しています。ZIPを正しく解凍したか確認してください。"
  exit 1
fi
echo "  すべてのファイルが存在します"

# 2. パーミッション設定
echo "[2/3] パーミッション設定..."
chmod 644 "\${CERT_DIR}/"*.pem.crt
chmod 600 "\${CERT_DIR}/"*.pem.key
chmod 644 "\${CERT_DIR}/AmazonRootCA1.pem"
chmod 644 "\${CERT_DIR}/config.json"
chmod +x "\${CERT_DIR}/"*.sh 2>/dev/null || true

# 3. 所有者確認・変更（必要な場合）
echo "[3/3] 所有者確認..."
CURRENT_USER=$(whoami)
FILE_OWNER=$(stat -c '%U' "\${CERT_DIR}/{{thingName}}.pem.key" 2>/dev/null || stat -f '%Su' "\${CERT_DIR}/{{thingName}}.pem.key" 2>/dev/null)

if [ "\${FILE_OWNER}" != "\${CURRENT_USER}" ]; then
  echo "  ファイル所有者: \${FILE_OWNER} → \${CURRENT_USER} に変更します"
  if [ "$(id -u)" -eq 0 ]; then
    # root で実行中
    echo "  警告: root で実行中です。適切なユーザーに所有者を変更してください:"
    echo "    sudo chown -R <username>:<username> \${CERT_DIR}"
  else
    # 一般ユーザーで実行中（sudo が必要）
    echo "  sudo を使用して所有者を変更します..."
    sudo chown -R "\${CURRENT_USER}:\${CURRENT_USER}" "\${CERT_DIR}" || {
      echo "  警告: 所有者の変更に失敗しました。手動で実行してください:"
      echo "    sudo chown -R \${CURRENT_USER}:\${CURRENT_USER} \${CERT_DIR}"
    }
  fi
else
  echo "  所有者: \${CURRENT_USER} (OK)"
fi

echo ""
echo "ファイル一覧:"
ls -la "\${CERT_DIR}/"
echo ""

echo "============================================"
echo "  セットアップ完了!"
echo "============================================"
echo ""
echo "接続テストを実行するには:"
echo "  ./connect-test.sh"
echo ""
`;

// connect-test.sh テンプレート
export const CONNECT_TEST_TEMPLATE = `#!/bin/bash
# ============================================================
# AWS IoT Core 接続テストスクリプト (curl版)
#
# Thing: {{thingName}}
# Endpoint: {{endpoint}}
#
# 使用方法:
#   ./connect-test.sh           # 接続テスト
#   ./connect-test.sh publish   # メッセージ送信テスト
#   ./connect-test.sh shadow    # Device Shadow 取得
#   ./connect-test.sh update    # Device Shadow 更新
#   ./connect-test.sh all       # 全テスト実行
#
# 追加パッケージ不要（curl のみ使用）
# ============================================================

set -e

# ============================================================
# 設定
# ============================================================
SCRIPT_DIR="$(cd "$(dirname "$0")" && pwd)"

# config.json から読み込み、なければスクリプトディレクトリをデフォルト使用
if [ -f "\${SCRIPT_DIR}/config.json" ]; then
    THING_NAME=$(grep -o '"thingName"[^,]*' "\${SCRIPT_DIR}/config.json" | cut -d'"' -f4)
    ENDPOINT=$(grep -o '"endpoint"[^,]*' "\${SCRIPT_DIR}/config.json" | cut -d'"' -f4)
    CERT_PATH=$(grep -o '"certPath"[^,]*' "\${SCRIPT_DIR}/config.json" | cut -d'"' -f4)
    KEY_PATH=$(grep -o '"keyPath"[^,]*' "\${SCRIPT_DIR}/config.json" | cut -d'"' -f4)
    CA_PATH=$(grep -o '"caPath"[^,]*' "\${SCRIPT_DIR}/config.json" | cut -d'"' -f4)
else
    # config.json がない場合はスクリプトディレクトリを使用
    THING_NAME="{{thingName}}"
    ENDPOINT="{{endpoint}}"
    CERT_PATH="\${SCRIPT_DIR}/{{thingName}}.pem.crt"
    KEY_PATH="\${SCRIPT_DIR}/{{thingName}}.pem.key"
    CA_PATH="\${SCRIPT_DIR}/AmazonRootCA1.pem"
fi

# AWS IoT HTTPS ポート
PORT=8443

# 色定義
RED='\\033[0;31m'
GREEN='\\033[0;32m'
YELLOW='\\033[1;33m'
BLUE='\\033[0;34m'
NC='\\033[0m'

# ============================================================
# ヘルパー関数
# ============================================================
log_info() {
    echo -e "\${BLUE}[INFO]\${NC} \$1"
}

log_success() {
    echo -e "\${GREEN}[OK]\${NC} \$1"
}

log_error() {
    echo -e "\${RED}[ERROR]\${NC} \$1"
}

log_warn() {
    echo -e "\${YELLOW}[WARN]\${NC} \$1"
}

# ============================================================
# 接続テスト（TLS接続確認）
# ============================================================
test_connection() {
    echo ""
    echo "=================================================="
    echo "  AWS IoT Core 接続テスト"
    echo "=================================================="
    echo ""
    echo "  Thing Name: \${THING_NAME}"
    echo "  Endpoint:   \${ENDPOINT}"
    echo ""

    log_info "証明書ファイルを確認中..."

    # ファイル存在確認
    local missing=0
    for file in "\${CERT_PATH}" "\${KEY_PATH}" "\${CA_PATH}"; do
        if [ ! -f "\${file}" ]; then
            log_error "ファイルが見つかりません: \${file}"
            missing=1
        fi
    done

    if [ \${missing} -eq 1 ]; then
        log_error "証明書ファイルが不足しています"
        return 1
    fi

    log_success "証明書ファイル確認OK"

    log_info "TLS接続をテスト中..."

    # curlで接続テスト（Device Shadowエンドポイントにアクセス）
    local response
    response=$(curl -s -w "\\n%{http_code}" \\
        --tlsv1.2 \\
        --cert "\${CERT_PATH}" \\
        --key "\${KEY_PATH}" \\
        --cacert "\${CA_PATH}" \\
        "https://\${ENDPOINT}:\${PORT}/things/\${THING_NAME}/shadow" \\
        2>&1) || true

    local http_code=$(echo "\${response}" | tail -n1)
    local body=$(echo "\${response}" | sed '\$d')

    if [ "\${http_code}" = "200" ] || [ "\${http_code}" = "404" ]; then
        log_success "TLS接続成功! (HTTP \${http_code})"
        if [ "\${http_code}" = "404" ]; then
            log_info "Shadow はまだ存在しません（初回接続）"
        fi
        return 0
    else
        log_error "接続失敗 (HTTP \${http_code})"
        echo "  レスポンス: \${body}"
        return 1
    fi
}

# ============================================================
# メッセージ送信テスト
# ============================================================
test_publish() {
    local topic="\${1:-test/\${THING_NAME}}"
    local message="\${2:-{\\"thingName\\":\\"\${THING_NAME}\\",\\"message\\":\\"Hello from device!\\",\\"timestamp\\":$(date +%s)}}"

    echo ""
    echo "=================================================="
    echo "  MQTT メッセージ送信テスト (HTTPS)"
    echo "=================================================="
    echo ""
    echo "  Topic:   \${topic}"
    echo "  Message: \${message}"
    echo ""

    log_info "メッセージを送信中..."

    local response
    response=$(curl -s -w "\\n%{http_code}" \\
        --tlsv1.2 \\
        --cert "\${CERT_PATH}" \\
        --key "\${KEY_PATH}" \\
        --cacert "\${CA_PATH}" \\
        -X POST \\
        -H "Content-Type: application/json" \\
        -d "\${message}" \\
        "https://\${ENDPOINT}:\${PORT}/topics/\${topic}?qos=1" \\
        2>&1)

    local http_code=$(echo "\${response}" | tail -n1)
    local body=$(echo "\${response}" | sed '\$d')

    if [ "\${http_code}" = "200" ]; then
        log_success "メッセージ送信成功!"
        echo ""
        log_info "AWS IoT コンソールの MQTT test client で確認できます"
        echo "  トピック: \${topic}"
        return 0
    else
        log_error "送信失敗 (HTTP \${http_code})"
        echo "  レスポンス: \${body}"
        return 1
    fi
}

# ============================================================
# Device Shadow 取得
# ============================================================
test_shadow_get() {
    echo ""
    echo "=================================================="
    echo "  Device Shadow 取得"
    echo "=================================================="
    echo ""

    log_info "Shadow を取得中..."

    local response
    response=$(curl -s -w "\\n%{http_code}" \\
        --tlsv1.2 \\
        --cert "\${CERT_PATH}" \\
        --key "\${KEY_PATH}" \\
        --cacert "\${CA_PATH}" \\
        "https://\${ENDPOINT}:\${PORT}/things/\${THING_NAME}/shadow" \\
        2>&1)

    local http_code=$(echo "\${response}" | tail -n1)
    local body=$(echo "\${response}" | sed '\$d')

    if [ "\${http_code}" = "200" ]; then
        log_success "Shadow 取得成功!"
        echo ""
        echo "現在の Shadow 状態:"
        echo "\${body}" | python3 -m json.tool 2>/dev/null || echo "\${body}"
        return 0
    elif [ "\${http_code}" = "404" ]; then
        log_warn "Shadow がまだ存在しません"
        return 0
    else
        log_error "取得失敗 (HTTP \${http_code})"
        echo "  レスポンス: \${body}"
        return 1
    fi
}

# ============================================================
# Device Shadow 更新
# ============================================================
test_shadow_update() {
    echo ""
    echo "=================================================="
    echo "  Device Shadow 更新"
    echo "=================================================="
    echo ""

    local timestamp=$(date -u +"%Y-%m-%dT%H:%M:%SZ")
    local payload="{\\"state\\":{\\"reported\\":{\\"status\\":\\"online\\",\\"lastConnected\\":\\"\${timestamp}\\",\\"testMode\\":true}}}"

    log_info "Shadow を更新中..."
    echo "  更新内容: \${payload}"
    echo ""

    local response
    response=$(curl -s -w "\\n%{http_code}" \\
        --tlsv1.2 \\
        --cert "\${CERT_PATH}" \\
        --key "\${KEY_PATH}" \\
        --cacert "\${CA_PATH}" \\
        -X POST \\
        -H "Content-Type: application/json" \\
        -d "\${payload}" \\
        "https://\${ENDPOINT}:\${PORT}/things/\${THING_NAME}/shadow" \\
        2>&1)

    local http_code=$(echo "\${response}" | tail -n1)
    local body=$(echo "\${response}" | sed '\$d')

    if [ "\${http_code}" = "200" ]; then
        log_success "Shadow 更新成功!"
        echo ""
        echo "更新後の Shadow:"
        echo "\${body}" | python3 -m json.tool 2>/dev/null || echo "\${body}"
        return 0
    else
        log_error "更新失敗 (HTTP \${http_code})"
        echo "  レスポンス: \${body}"
        return 1
    fi
}

# ============================================================
# 使用方法
# ============================================================
show_usage() {
    echo ""
    echo "使用方法:"
    echo "  ./connect-test.sh [コマンド]"
    echo ""
    echo "コマンド:"
    echo "  (なし)    接続テストのみ"
    echo "  publish   メッセージ送信テスト"
    echo "  shadow    Device Shadow 取得"
    echo "  update    Device Shadow 更新"
    echo "  all       全テスト実行"
    echo ""
    echo "例:"
    echo "  ./connect-test.sh"
    echo "  ./connect-test.sh publish"
    echo "  ./connect-test.sh all"
    echo ""
}

# ============================================================
# メイン
# ============================================================
main() {
    local cmd="\${1:-}"
    local results=()
    local all_passed=1

    case "\${cmd}" in
        "publish")
            test_connection && results+=("接続テスト:PASS") || { results+=("接続テスト:FAIL"); all_passed=0; }
            test_publish && results+=("送信テスト:PASS") || { results+=("送信テスト:FAIL"); all_passed=0; }
            ;;
        "shadow")
            test_connection && results+=("接続テスト:PASS") || { results+=("接続テスト:FAIL"); all_passed=0; }
            test_shadow_get && results+=("Shadow取得:PASS") || { results+=("Shadow取得:FAIL"); all_passed=0; }
            ;;
        "update")
            test_connection && results+=("接続テスト:PASS") || { results+=("接続テスト:FAIL"); all_passed=0; }
            test_shadow_update && results+=("Shadow更新:PASS") || { results+=("Shadow更新:FAIL"); all_passed=0; }
            ;;
        "all")
            test_connection && results+=("接続テスト:PASS") || { results+=("接続テスト:FAIL"); all_passed=0; }
            test_publish && results+=("送信テスト:PASS") || { results+=("送信テスト:FAIL"); all_passed=0; }
            test_shadow_get && results+=("Shadow取得:PASS") || { results+=("Shadow取得:FAIL"); all_passed=0; }
            test_shadow_update && results+=("Shadow更新:PASS") || { results+=("Shadow更新:FAIL"); all_passed=0; }
            ;;
        "-h"|"--help"|"help")
            show_usage
            exit 0
            ;;
        "")
            test_connection && results+=("接続テスト:PASS") || { results+=("接続テスト:FAIL"); all_passed=0; }
            ;;
        *)
            log_error "不明なコマンド: \${cmd}"
            show_usage
            exit 1
            ;;
    esac

    # 結果表示
    echo ""
    echo "=================================================="
    echo "  テスト結果"
    echo "=================================================="
    echo ""

    for r in "\${results[@]}"; do
        local name=$(echo "\${r}" | cut -d: -f1)
        local status=$(echo "\${r}" | cut -d: -f2)
        if [ "\${status}" = "PASS" ]; then
            echo -e "  \${name}: \${GREEN}PASS\${NC}"
        else
            echo -e "  \${name}: \${RED}FAIL\${NC}"
        fi
    done

    echo ""

    if [ \${all_passed} -eq 1 ]; then
        log_success "テスト完了!"
        exit 0
    else
        log_error "一部のテストが失敗しました"
        exit 1
    fi
}

main "\$@"
`;

/**
 * テンプレートのプレースホルダーを置換
 * @param {string} template - テンプレート文字列
 * @param {object} config - 置換する値 { thingName, endpoint, region }
 * @returns {string} - 置換後の文字列
 */
export function applyTemplate(template, config) {
  return template
    .replace(/\{\{thingName\}\}/g, config.thingName)
    .replace(/\{\{endpoint\}\}/g, config.endpoint)
    .replace(/\{\{region\}\}/g, config.region);
}
